<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useNumberFormatter } from 'shared/composables/useNumberFormatter';
import { useAlert } from 'dashboard/composables';
import ImovelAPI from 'dashboard/api/imoveis';

const props = defineProps({
  offers: {
    type: Array,
    default: () => [],
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['saveAnalysis']);

const { formatCurrency } = useNumberFormatter();
const INCOME_COMMITMENT_LIMIT = 0.3;

const selectedOfferId = ref('');
const simulations = ref({});
const isLoading = ref(false);
const checklist = reactive({
  documents_received: false,
  income_verified: false,
  fgts_checked: false,
  bank_pre_approved: false,
});
const form = reactive({
  property_value: '',
  down_payment: '',
  term_months: 360,
  system: 'price',
  annual_rate: '',
  monthly_income: '',
  age: '',
});

const financeableOffers = computed(() =>
  props.offers.filter(offer => offer.business_context === 'purchase')
);

const selectedOffer = computed(() =>
  financeableOffers.value.find(
    offer => String(offer.id) === selectedOfferId.value
  )
);

const primaryResult = computed(() => simulations.value[form.system]);
const secondarySystem = computed(() =>
  form.system === 'price' ? 'sac' : 'price'
);
const secondaryResult = computed(
  () => simulations.value[secondarySystem.value]
);

const toNumber = value => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatBRL = value =>
  value == null || value === '' ? '-' : formatCurrency(Number(value));

const formatPercent = value =>
  value == null ? '-' : `${Math.round(Number(value) * 100)}%`;

const incomeCommitmentValue = result => {
  const value = Number(result?.income_commitment);
  return Number.isFinite(value) ? value : null;
};

const incomeCommitmentMeta = result => {
  const commitment = incomeCommitmentValue(result);
  if (commitment == null) {
    return { label: '', className: 'text-[#1a1a1a]' };
  }

  if (commitment <= INCOME_COMMITMENT_LIMIT) {
    return { label: 'Renda compatível', className: 'text-emerald-700' };
  }

  return { label: 'Acima de 30%', className: 'text-rose-700' };
};

const offerLabel = offer =>
  [
    offer.imovel_titulo || 'Imóvel sem título',
    offer.contact_name || offer.contact_phone_number,
  ]
    .filter(Boolean)
    .join(' · ');

const applyOfferDefaults = offer => {
  if (!offer) return;

  const propertyValue = toNumber(
    offer.imovel_valor_venda || offer.offer_amount
  );
  const financedAmount = toNumber(offer.financed_amount);
  form.property_value = propertyValue || '';
  form.down_payment =
    propertyValue && financedAmount
      ? Math.max(propertyValue - financedAmount, 0)
      : Math.round(propertyValue * 0.2);
  form.term_months = 360;
  simulations.value = {};
};

const ensureSelectedOffer = () => {
  if (selectedOffer.value) return;

  const preferred =
    financeableOffers.value.find(
      offer =>
        ['financiamento', 'financiamento_fgts'].includes(offer.payment_kind) &&
        offer.status === 'accepted'
    ) ||
    financeableOffers.value.find(offer =>
      ['financiamento', 'financiamento_fgts'].includes(offer.payment_kind)
    ) ||
    financeableOffers.value[0];

  selectedOfferId.value = preferred ? String(preferred.id) : '';
  applyOfferDefaults(preferred);
};

watch(
  () => props.offers,
  () => ensureSelectedOffer(),
  { immediate: true }
);

watch(selectedOfferId, () => {
  applyOfferDefaults(selectedOffer.value);
});

const payloadFor = system => ({
  property_value: form.property_value,
  down_payment: form.down_payment,
  term_months: form.term_months,
  system,
  annual_rate: form.annual_rate,
  monthly_income: form.monthly_income,
  age: form.age,
});

const runSimulation = async system => {
  const { data } = await ImovelAPI.simulateFinancing(
    selectedOffer.value.imovel_id,
    payloadFor(system)
  );
  return data;
};

const simulate = async () => {
  if (!selectedOffer.value) {
    useAlert('Selecione uma proposta de venda para simular o financiamento.');
    return;
  }

  isLoading.value = true;
  simulations.value = {};
  try {
    const systems = ['price', 'sac'];
    const results = await Promise.allSettled(
      systems.map(system => runSimulation(system))
    );
    simulations.value = systems.reduce((memo, system, index) => {
      const result = results[index];
      memo[system] =
        result.status === 'fulfilled'
          ? result.value
          : result.reason?.response?.data || {
              valid: false,
              errors: [{ message: 'Simulação indisponível.' }],
            };
      return memo;
    }, {});
  } catch {
    useAlert('Não foi possível simular o financiamento.');
  } finally {
    isLoading.value = false;
  }
};

const resultErrors = result =>
  Array.isArray(result?.errors)
    ? result.errors.map(error => error.message)
    : [];

const saveAnalysis = () => {
  if (!primaryResult.value?.valid) {
    useAlert('Simule uma proposta válida antes de salvar a análise.');
    return;
  }

  emit('saveAnalysis', {
    offer: selectedOffer.value,
    result: primaryResult.value,
    simulations: simulations.value,
    monthlyIncome: form.monthly_income,
    checklist: { ...checklist },
  });
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <article
    class="rounded-lais border border-[#dbe4ef] bg-white p-5 shadow-lais-card"
  >
    <div
      class="mb-4 flex flex-wrap items-start justify-between gap-3 border-b border-[#f1f5f9] pb-4"
    >
      <div>
        <h2 class="font-redHatDisplay text-[24px] font-semibold text-[#1a1a1a]">
          Simulador de financiamento
        </h2>
        <p class="mt-1 max-w-3xl text-[13px] text-[#718096]">
          Compare SAC e PRICE, valide renda e salve o acompanhamento de crédito
          na proposta.
        </p>
      </div>
      <span
        class="rounded-full bg-emerald-50 px-3 py-1 text-[12px] font-semibold text-emerald-700"
      >
        Sem open banking
      </span>
    </div>

    <div class="grid gap-5 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
      <div class="grid gap-4">
        <label class="grid gap-1.5 text-[12px] text-[#718096]">
          Proposta
          <select
            v-model="selectedOfferId"
            class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
          >
            <option value="">Selecione</option>
            <option
              v-for="offer in financeableOffers"
              :key="offer.id"
              :value="String(offer.id)"
            >
              {{ offerLabel(offer) }}
            </option>
          </select>
        </label>

        <div class="grid gap-3 sm:grid-cols-2">
          <label class="grid gap-1.5 text-[12px] text-[#718096]">
            Valor do imóvel
            <input
              v-model="form.property_value"
              type="number"
              min="0"
              step="1000"
              class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
            />
          </label>
          <label class="grid gap-1.5 text-[12px] text-[#718096]">
            Entrada
            <input
              v-model="form.down_payment"
              type="number"
              min="0"
              step="1000"
              class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
            />
          </label>
          <label class="grid gap-1.5 text-[12px] text-[#718096]">
            Prazo
            <input
              v-model="form.term_months"
              type="number"
              min="1"
              max="420"
              step="12"
              class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
            />
          </label>
          <label class="grid gap-1.5 text-[12px] text-[#718096]">
            Renda mensal
            <input
              v-model="form.monthly_income"
              type="number"
              min="0"
              step="500"
              class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
            />
          </label>
          <label class="grid gap-1.5 text-[12px] text-[#718096]">
            Idade
            <input
              v-model="form.age"
              type="number"
              min="18"
              max="90"
              class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
            />
          </label>
          <label class="grid gap-1.5 text-[12px] text-[#718096]">
            Taxa anual
            <input
              v-model="form.annual_rate"
              type="number"
              min="0"
              max="1"
              step="0.0001"
              placeholder="Padrão"
              class="rounded-lais border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#1a1a1a]"
            />
          </label>
        </div>

        <div class="flex flex-wrap gap-2">
          <button
            v-for="system in ['price', 'sac']"
            :key="system"
            type="button"
            class="rounded-full border px-4 py-2 text-[12px] font-semibold uppercase transition"
            :class="
              form.system === system
                ? 'border-[#49317a] bg-[#49317a] text-white'
                : 'border-[#d6dce5] text-[#4a5568] hover:border-[#49317a]'
            "
            @click="form.system = system"
          >
            {{ system }}
          </button>
        </div>

        <div class="grid gap-2 rounded-lais-sm bg-[#f8fafc] p-3">
          <label
            v-for="(value, key) in checklist"
            :key="key"
            class="flex items-center gap-2 text-[12px] font-semibold text-[#4a5568]"
          >
            <input v-model="checklist[key]" type="checkbox" class="size-4" />
            {{
              {
                documents_received: 'Documentos recebidos',
                income_verified: 'Renda conferida',
                fgts_checked: 'FGTS conferido',
                bank_pre_approved: 'Pré-aprovação bancária',
              }[key]
            }}
          </label>
        </div>

        <div class="flex flex-wrap gap-3">
          <button
            type="button"
            class="inline-flex h-10 items-center justify-center gap-2 rounded-lais bg-[#49317a] px-4 text-[13px] font-semibold text-white transition hover:bg-[#3d2966] disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isLoading"
            @click="simulate"
          >
            <span class="i-lucide-calculator size-4" aria-hidden="true" />
            {{ isLoading ? 'Simulando...' : 'Simular' }}
          </button>
          <button
            type="button"
            class="inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-[#d6dce5] px-4 text-[13px] font-semibold text-[#4a5568] transition hover:bg-[#f8fafc] disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isSaving || !primaryResult?.valid"
            @click="saveAnalysis"
          >
            <span class="i-lucide-clipboard-check size-4" aria-hidden="true" />
            {{ isSaving ? 'Salvando...' : 'Salvar análise' }}
          </button>
        </div>
      </div>

      <div class="grid content-start gap-3">
        <div v-if="primaryResult" class="grid gap-3 md:grid-cols-2">
          <article
            v-for="result in [primaryResult, secondaryResult].filter(Boolean)"
            :key="result.system"
            class="rounded-lais border border-[#eef1f6] p-4"
          >
            <div class="flex items-center justify-between gap-3">
              <strong
                class="text-[13px] font-semibold uppercase text-[#49317a]"
              >
                {{ result.system }}
              </strong>
              <span
                class="rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase"
                :class="
                  result.valid
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-rose-50 text-rose-700'
                "
              >
                {{ result.valid ? 'Válida' : 'Revisar' }}
              </span>
            </div>

            <div v-if="result.valid" class="mt-4 grid gap-2 text-[13px]">
              <div class="flex justify-between gap-3">
                <span class="text-[#718096]">Financiado</span>
                <strong>{{ formatBRL(result.financed_amount) }}</strong>
              </div>
              <div class="flex justify-between gap-3">
                <span class="text-[#718096]">Primeira parcela</span>
                <strong>{{ formatBRL(result.first_installment) }}</strong>
              </div>
              <div class="flex justify-between gap-3">
                <span class="text-[#718096]">Última parcela</span>
                <strong>{{ formatBRL(result.last_installment) }}</strong>
              </div>
              <div class="flex justify-between gap-3">
                <span class="text-[#718096]">Juros totais</span>
                <strong>{{ formatBRL(result.total_interest) }}</strong>
              </div>
              <div class="flex justify-between gap-3">
                <span class="text-[#718096]">Total pago</span>
                <strong>{{ formatBRL(result.total_paid) }}</strong>
              </div>
              <div class="flex justify-between gap-3">
                <span class="text-[#718096]">Comprometimento</span>
                <strong
                  class="inline-flex flex-wrap justify-end gap-1 text-right"
                  :class="incomeCommitmentMeta(result).className"
                >
                  <span>{{ formatPercent(result.income_commitment) }}</span>
                  <span v-if="incomeCommitmentMeta(result).label">
                    · {{ incomeCommitmentMeta(result).label }}
                  </span>
                </strong>
              </div>
            </div>
            <ul v-else class="mt-4 grid gap-1 text-[12px] text-rose-700">
              <li v-for="message in resultErrors(result)" :key="message">
                {{ message }}
              </li>
            </ul>
          </article>
        </div>

        <div
          v-else
          class="rounded-lais border border-dashed border-[#d6dce5] p-6 text-[13px] text-[#718096]"
        >
          Selecione uma proposta de venda e rode a simulação.
        </div>
      </div>
    </div>
  </article>
</template>
